package es.subastia.ui.components

import androidx.compose.foundation.background
import androidx.compose.foundation.layout.Arrangement
import androidx.compose.foundation.layout.Box
import androidx.compose.foundation.layout.BoxScope
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.Row
import androidx.compose.foundation.layout.Spacer
import androidx.compose.foundation.layout.fillMaxSize
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.height
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.layout.size
import androidx.compose.foundation.layout.width
import androidx.compose.foundation.shape.RoundedCornerShape
import androidx.compose.material.icons.Icons
import androidx.compose.material.icons.filled.DateRange
import androidx.compose.material.icons.filled.LocationOn
import androidx.compose.material3.Card
import androidx.compose.material3.CardDefaults
import androidx.compose.material3.Icon
import androidx.compose.material3.MaterialTheme
import androidx.compose.material3.Surface
import androidx.compose.material3.Text
import androidx.compose.runtime.Composable
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.graphics.Brush
import androidx.compose.ui.graphics.Color
import androidx.compose.ui.layout.ContentScale
import androidx.compose.ui.text.font.FontWeight
import androidx.compose.ui.text.style.TextOverflow
import androidx.compose.ui.unit.dp
import coil3.compose.AsyncImage
import es.subastia.domain.model.AuctionProcedureType
import es.subastia.ui.model.AuctionUiModel
import es.subastia.utils.formatMoney
import es.subastia.utils.isToday
import es.subastia.utils.toUserFriendlyDate
import org.jetbrains.compose.resources.stringResource
import subastia.composeapp.generated.resources.Res
import subastia.composeapp.generated.resources.label_active
import subastia.composeapp.generated.resources.label_auction_value
import subastia.composeapp.generated.resources.label_claimed_amount
import subastia.composeapp.generated.resources.label_lots_count
import subastia.composeapp.generated.resources.label_new_auction
import org.jetbrains.compose.ui.tooling.preview.Preview
import es.subastia.ui.model.AuctionDatesUiModel

@Composable
fun RichAuctionCard(
    auction: AuctionUiModel,
    onClick: () -> Unit,
    modifier: Modifier = Modifier,
    trailingContent: @Composable (BoxScope.() -> Unit)? = null,
) {
    Card(
        onClick = onClick,
        shape = RoundedCornerShape(16.dp),
        elevation = CardDefaults.cardElevation(defaultElevation = 4.dp),
        colors = CardDefaults.cardColors(containerColor = MaterialTheme.colorScheme.surface),
        modifier = modifier.fillMaxWidth(),
    ) {
        Column {
            // Image Area
            Box(
                modifier = Modifier
                    .fillMaxWidth()
                    .height(160.dp)
                    .background(MaterialTheme.colorScheme.surfaceVariant),
            ) {
                if (!auction.imageUrl.isNullOrBlank()) {
                    AsyncImage(
                        model = auction.imageUrl,
                        contentDescription = auction.title,
                        contentScale = ContentScale.Crop,
                        modifier = Modifier.fillMaxSize(),
                    )
                }

                Box(
                    Modifier.fillMaxSize().background(
                        Brush.verticalGradient(
                            listOf(Color.Transparent, Color.Black.copy(alpha = 0.4f)),
                        ),
                    ),
                )

                // Status & New Badge Container
                Column(
                    modifier = Modifier.align(Alignment.TopEnd).padding(8.dp),
                    horizontalAlignment = Alignment.End,
                    verticalArrangement = Arrangement.spacedBy(4.dp),
                ) {
                    if (auction.updatedAt?.isToday() == true) {
                        Surface(
                            color = MaterialTheme.colorScheme.primary,
                            shape = RoundedCornerShape(4.dp),
                        ) {
                            Text(
                                text = stringResource(Res.string.label_new_auction),
                                style = MaterialTheme.typography.labelSmall,
                                color = MaterialTheme.colorScheme.onPrimary,
                                fontWeight = FontWeight.Bold,
                                modifier = Modifier.padding(horizontal = 6.dp, vertical = 2.dp),
                            )
                        }
                    }

                    auction.status?.let { status ->
                        Surface(
                            color = if (status == "ACTIVE") Color(0xFF4CAF50) else Color.Gray,
                            shape = RoundedCornerShape(4.dp),
                        ) {
                            Text(
                                text = if (status == "ACTIVE") stringResource(Res.string.label_active) else status,
                                style = MaterialTheme.typography.labelSmall,
                                color = Color.White,
                                fontWeight = FontWeight.Bold,
                                modifier = Modifier.padding(horizontal = 6.dp, vertical = 2.dp),
                            )
                        }
                    }
                }

                // Lots Badge
                if (auction.lots.size > 1) {
                    Surface(
                        color = MaterialTheme.colorScheme.tertiary,
                        shape = RoundedCornerShape(4.dp),
                        modifier = Modifier.padding(8.dp).align(Alignment.BottomEnd).padding(bottom = 32.dp),
                    ) {
                        Text(
                            text = stringResource(Res.string.label_lots_count, auction.lots.size),
                            style = MaterialTheme.typography.labelSmall,
                            color = MaterialTheme.colorScheme.onTertiary,
                            fontWeight = FontWeight.Bold,
                            modifier = Modifier.padding(horizontal = 6.dp, vertical = 2.dp),
                        )
                    }
                }

                // City Overlay
                auction.city?.let {
                    Row(
                        Modifier.align(Alignment.BottomStart).padding(12.dp),
                        verticalAlignment = Alignment.CenterVertically,
                    ) {
                        Icon(Icons.Default.LocationOn, null, tint = Color.White, modifier = Modifier.size(14.dp))
                        Spacer(Modifier.width(4.dp))
                        Text(it, color = Color.White, style = MaterialTheme.typography.labelMedium)
                    }
                }

                // Procedure Type Badge
                if (auction.procedureType != AuctionProcedureType.UNKNOWN) {
                    Surface(
                        color = Color(auction.procedureType.colorHex),
                        shape = RoundedCornerShape(bottomEnd = 8.dp),
                        modifier = Modifier.align(Alignment.TopStart),
                    ) {
                        Text(
                            text = auction.procedureType.label,
                            style = MaterialTheme.typography.labelSmall,
                            color = Color.White,
                            fontWeight = FontWeight.Bold,
                            modifier = Modifier.padding(horizontal = 8.dp, vertical = 4.dp),
                        )
                    }
                }

                // Optional Trailing Content (like Delete button)
                trailingContent?.invoke(this)
            }

            // Info Content
            Column(Modifier.padding(16.dp)) {
                Text(
                    text = auction.title,
                    style = MaterialTheme.typography.titleMedium,
                    fontWeight = FontWeight.Bold,
                    maxLines = 2,
                    overflow = TextOverflow.Ellipsis,
                )

                Spacer(Modifier.height(8.dp))

                Row(
                    Modifier.fillMaxWidth(),
                    horizontalArrangement = Arrangement.SpaceBetween,
                    verticalAlignment = Alignment.CenterVertically,
                ) {
                    // End Date
                    auction.dates.endDate?.let { date ->
                        Row(verticalAlignment = Alignment.CenterVertically) {
                            Icon(Icons.Default.DateRange, null, modifier = Modifier.size(16.dp), tint = MaterialTheme.colorScheme.secondary)
                            Spacer(Modifier.width(4.dp))
                            Text(
                                text = date.toUserFriendlyDate(),
                                style = MaterialTheme.typography.labelSmall,
                                color = MaterialTheme.colorScheme.onSurface,
                            )
                        }
                    }

                    Text(
                        text = auction.auctionId,
                        style = MaterialTheme.typography.labelSmall,
                        color = MaterialTheme.colorScheme.onSurfaceVariant,
                    )
                }

                Spacer(Modifier.height(16.dp))

                // Prices
                Row(
                    modifier = Modifier.fillMaxWidth(),
                    horizontalArrangement = Arrangement.SpaceBetween,
                    verticalAlignment = Alignment.CenterVertically,
                ) {
                    if (auction.claimedAmount > 0) {
                        Column(horizontalAlignment = Alignment.Start) {
                            Text(
                                text = stringResource(Res.string.label_claimed_amount),
                                style = MaterialTheme.typography.labelSmall,
                                color = MaterialTheme.colorScheme.onSurfaceVariant,
                            )
                            Text(
                                text = "${auction.claimedAmount.formatMoney()} €",
                                style = MaterialTheme.typography.titleMedium,
                                fontWeight = FontWeight.Bold,
                                color = MaterialTheme.colorScheme.onSurface,
                            )
                        }
                    } else {
                        Spacer(Modifier.weight(1f))
                    }

                    Column(horizontalAlignment = Alignment.End) {
                        Text(
                            text = stringResource(Res.string.label_auction_value),
                            style = MaterialTheme.typography.labelSmall,
                            color = MaterialTheme.colorScheme.onSurfaceVariant,
                        )
                        Text(
                            text = "${auction.auctionValue.formatMoney()} €",
                            style = MaterialTheme.typography.titleMedium,
                            fontWeight = FontWeight.Bold,
                            color = MaterialTheme.colorScheme.onSurface,
                        )
                    }
                }
            }
        }
    }
}

@Preview
@Composable
fun RichAuctionCardPreview() {
    MaterialTheme {
        RichAuctionCard(
            auction = AuctionUiModel(
                auctionId = "SUB-JA-2024-123456",
                title = "Vivienda en Calle Mayor 1, Madrid",
                currentPrice = 120000.0,
                auctionValue = 200000.0,
                appraisalValue = 220000.0,
                depositAmount = 10000.0,
                claimedAmount = 50000.0,
                bidStep = 1000.0,
                dates = AuctionDatesUiModel("2024-01-01", "2024-12-31"),
                city = "Madrid",
                province = "Madrid",
                status = "ACTIVE",
                type = "Inmuebles",
                hasLots = false,
                address = "Calle Mayor 1",
                model = null,
                portalUrl = "https://subastas.boe.es",
                procedureType = AuctionProcedureType.JUDICIAL_APREMIO,
            ),
            onClick = {},
        )
    }
}
